
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Hooks
import useFriends from "./hooks/useFriends";
import useFriendRequests from "./hooks/useFriendRequests";
import { useAuth } from "./hooks/useAuth";
import useNotifications from "./hooks/useNotifications";
import useBlockUser from "./hooks/useBlockUser";
import useChatSettings from "./hooks/useChatSettings";

// Firebase
import { auth, firestore } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";

// Styles & Assets
import "../Styles/friends.css";
import BellIcon from "../assets/icons/Bell.png";
import SettingsIcon from "../assets/icons/Settings.svg";
import DefaultAvatar from "../assets/images/3d_avatar_16.png";
import MessageIcon from "../assets/images/message.png";

const DEFAULT_AVATAR = DefaultAvatar;

export default function Friends() {
  const navigate = useNavigate();
  const location = useLocation();

  // Hooks
  const { friends, loading, error, unfriend, muteUser, unmuteUser } = useFriends();
  const { user, userProfile } = useAuth();
  const { requestPermission } = useNotifications();
  const {
    requests: friendRequests,
    loading: reqLoading,
    error: reqError,
    acceptRequest,
    declineRequest,
  } = useFriendRequests();
  const { toggleLimit } = useChatSettings();
  const { blockUser, unblockUser, blockedUsers } = useBlockUser();

  // State
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeOptionsMenu, setActiveOptionsMenu] = useState(null);
  const [chatLimits, setChatLimits] = useState({});
  // For sorting by recent message
  const [recentTimestamps, setRecentTimestamps] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  // Track most recent message timestamp for each friend
  useEffect(() => {
    if (!user || friends.length === 0) return;
    const unsubscribes = friends.map((f) => {
      const friendId = f.id;
      if (!friendId) return () => {};
      const chatId = [user.uid, friendId].sort((a, b) => a.localeCompare(b)).join("_");
      const messagesRef = collection(firestore, "chats", chatId, "messages");
      return onSnapshot(messagesRef, (snapshot) => {
        let latest = 0;
        snapshot.forEach((doc) => {
          const ts = doc.data().timestamp;
          if (ts && ts.seconds && ts.seconds > latest) latest = ts.seconds;
        });
        setRecentTimestamps((prev) => ({ ...prev, [friendId]: latest }));
      });
    });
    return () => unsubscribes.forEach((u) => u());
  }, [user, friends]);

  // When coming back from messages.jsx, force a refresh to re-sort by recent message
  useEffect(() => {
    if (location.state && location.state.fromMessages) {
      setRefreshKey((k) => k + 1);
    }
  }, [location.state]);


  // Request notification permission on mount
  useEffect(() => {
    requestPermission();
  }, [requestPermission]);

  // Track per-friend "Limit Notifications" flag from chat doc

  // Track per-friend "Limit Notifications" flag from chat doc
  useEffect(() => {
    if (!user || friends.length === 0) return;

    const setLimitNotifications = (friend, snap) => {
      setChatLimits((prev) => ({
        ...prev,
        [friend.id]: snap.data()?.limitNotifications || false,
      }));
    };

    const subscribeLimitNotifications = (friend) => {
      const chatId = [user.uid, friend.id].sort((a, b) => a.localeCompare(b)).join("_");
      const chatRef = doc(firestore, "chats", chatId);
      return onSnapshot(chatRef, (snap) => setLimitNotifications(friend, snap));
    };
// Subscribe to all friends' chat docs for limitNotifications
    const unsubscribes = friends.map(subscribeLimitNotifications);
    return () => unsubscribes.forEach((u) => u());
  }, [friends, user]);

  // unread message dots in sidebar

  // Track unread message counts per friend
  useEffect(() => {
    if (!user || friends.length === 0) return;

    const setUnreadCount = (friend, snapshot) => {
      setUnreadCounts((prev) => ({
        ...prev,
        [friend.id]: snapshot.size,
      }));
    };

    const subscribeUnreadCount = (friend) => {
      const chatId = [user.uid, friend.id].sort((a, b) => a.localeCompare(b)).join("_");
      const messagesRef = collection(firestore, "chats", chatId, "messages");
      const q = query(
        messagesRef,
        where("read", "==", false),
        where("senderId", "==", friend.id)
      );
      return onSnapshot(q, (snapshot) => setUnreadCount(friend, snapshot));
    };

    const unsubscribes = friends.map(subscribeUnreadCount);
    return () => unsubscribes.forEach((unsub) => unsub());
  }, [friends, user]);


  // Handlers
  const handleChatClick = (friendId) => {
    navigate("/messages", { state: { friendId } });
  };

  const handleBlockToggle = (friendId) => {
    const isBlocked = blockedUsers.some(user => user.id === friendId);
    if (isBlocked) {
      unblockUser(friendId);
    } else {
      blockUser(friendId);
    }
    setActiveOptionsMenu(null);
  };

  const handleMuteToggle = (friend) => {
    if (friend.isMuted) {
      unmuteUser(friend.id);
    } else {
      muteUser(friend.id);
    }
    setActiveOptionsMenu(null);
  };

  const handleLimitToggle = (friendId) => {
    toggleLimit(friendId, chatLimits[friendId]);
    setActiveOptionsMenu(null);
  };

  const handleUnfriend = (friendId) => {
    if (window.confirm("Are you sure you want to unfriend this user?")) {
      unfriend(friendId);
      setActiveOptionsMenu(null);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };


  // Loading & error states
  if (loading) return <div>Loading friends...</div>;
  if (error) return <div>{error}</div>;

  // Filter and sort friends (hide blocked, sort by recent message, then unread count, then name)
  const visibleFriends = friends
    .filter((f) => !blockedUsers.some(user => user.id === f.id))
    .sort((a, b) => {
      const recentA = recentTimestamps[a.id] || 0;
      const recentB = recentTimestamps[b.id] || 0;
      if (recentB !== recentA) return recentB - recentA;
      const unreadA = unreadCounts[a.id] || 0;
      const unreadB = unreadCounts[b.id] || 0;
      if (unreadB !== unreadA) return unreadB - unreadA;
      return (a.name || '').localeCompare(b.name || '');
    });

  // =====================
  // Render
  // =====================
  return (
    <div className="container">
      {/* Sidebar */}
      <div className="sidebar">
        <h2>Direct Messages</h2>
        <div className="dm-list" key={refreshKey}>
          {visibleFriends.map((friend) => (
            <div className="dm" key={friend.id} onClick={() => handleChatClick(friend.id)}>
              <div className="dm-avatar">
                <img
                  src={friend.avatar || DEFAULT_AVATAR}
                  alt={friend.name}
                  onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                />
                {unreadCounts[friend.id] > 0 && <span className="unread-dot"></span>}
              </div>
              <span className="dm-name">{friend.name}</span>
            </div>
          ))}
        </div>
        <div className="bottom-section">
          <button
            className="settings-btn"
            type="button"
            onClick={() => navigate("/settings")}
            aria-label="Settings"
          >
            <img src={SettingsIcon} alt="Settings" />
          </button>
          <div className="user" onClick={() => navigate("/account")}> 
            <img
              src={userProfile?.avatar || user?.photoURL || "/images/bonfire.png"}
              alt="User"
              onError={(e) => (e.currentTarget.src = "/images/bonfire.png")}
            />
            <span style={{ display: "block", textAlign: "center", width: "100%" }}>{user?.displayName}</span>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="main">
        <div className="main-header">
          <h1>Friends Page</h1>

          <div className="header-right">
            <button className="add-friend" onClick={() => navigate("/addfriends")}>Add Friend</button>

            <div className="notif-container">
              <button className="notif-btn" onClick={() => setShowNotifications((s) => !s)}>
                <img src={BellIcon} alt="Notifications" />
                {friendRequests.length > 0 && <span className="notif-dot"></span>}
              </button>

              {showNotifications && (
                <div className="notif-dropdown">
                  <h3>Notifications</h3>
                  <ul>
                    {friendRequests.length === 0 ? (
                      <li>No new notifications</li>
                    ) : (
                      friendRequests.map((req) => (
                        <li key={req.id}>
                          New friend request from {req.fromName || "Someone"}
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>

            <button className="sign-out-btn" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>

        {/* Friend Requests */}
        <div className="friend-requests-container">
          <h3>Friend Requests</h3>
          {(() => {
            if (reqLoading) return <p>Loading...</p>;
            if (reqError) return <p>Error: {reqError}</p>;
            if (friendRequests.length === 0) return <p>You have no pending friend requests.</p>;
            return (
              <ul className="friend-requests-list">
                {friendRequests.map((request) => (
                  <li key={request.id} className="friend-request-item">
                    <img
                      src={request.fromAvatar || DEFAULT_AVATAR}
                      alt={request.fromName || "User"}
                      onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                    />
                    <span>{request.fromName || "Unknown"}</span>
                    <div className="request-buttons">
                      <button
                        type="button"
                        onClick={() => acceptRequest(request.id, request.from)}
                        className="accept-btn"
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={() => declineRequest(request.id)}
                        className="decline-btn"
                      >
                        Decline
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>

        {/* Friends list */}
        <div className="friends-container">
          {visibleFriends.map((friend) => (
            <div className="friend-card" key={friend.id}>
              <img
                src={friend.avatar || DEFAULT_AVATAR}
                alt={friend.name}
                onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
              />

              {/* Friend name is just text, not a DM link */}
              <span style={{ display: "block", textAlign: "center", width: "100%" }}>{friend.name}</span>

              {/* ✅ Click message bubble → go to Messages.jsx for that friend */}
              <button
                type="button"
                className="chat-btn"
                onClick={() => handleChatClick(friend.id)}
              >
                <img src={MessageIcon} alt="Message" style={{width: 24, height: 24}} />
              </button>

              <div className="options-wrapper">
                <button
                  type="button"
                  className="options-btn"
                  onClick={() =>
                    setActiveOptionsMenu(activeOptionsMenu === friend.id ? null : friend.id)
                  }
                >
                  ⋮
                </button>

                <div className={`context-menu ${activeOptionsMenu === friend.id ? "show" : ""}`}>
                  <button type="button" className="menu-item" onClick={() => handleUnfriend(friend.id)}>
                    Unfriend
                  </button>

                  <button type="button" className="menu-item" onClick={() => handleMuteToggle(friend)}>
                    {friend.isMuted ? "Unmute" : "Mute"}
                  </button>

                  <button type="button" className="menu-item" onClick={() => handleLimitToggle(friend.id)}>
                    {chatLimits[friend.id] ? "Disable Limit" : "Limit Notifications"}
                  </button>

                  <button type="button" className="menu-item" onClick={() => handleBlockToggle(friend.id)}>
                    {blockedUsers.includes(friend.id) ? "Unblock" : "Block"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
